import type { LargeShiftDetailsProps } from '@tempmee/react-blocks';
import type { IJob } from '~/models';
import { formatMoney } from '~/utils/formatMoney';
import { computeHours, resolveProfessionName } from '~/utils/shiftCard';

/**
 * Fields an open shift carries that the shared `IJob` model does not declare —
 * `procedures` lives on `IShift` and `autofillOptions` is a raw backend field.
 * Widened locally, as the legacy `ShiftDetailsInfo` does, rather than mutating
 * the shared model.
 */
export type TOpenShiftJob = IJob & {
  readonly procedures?: string[];
  readonly autofillOptions?: { favorite?: boolean };
};

/** The `LargeShiftDetails` props this page derives; the rest are block defaults. */
export type TShiftSummary = Pick<
  LargeShiftDetailsProps,
  'status' | 'date' | 'startTime' | 'endTime' | 'totalHours' | 'position' | 'rate'
>;



/**
 * Maps an open shift onto the `LargeShiftDetails` summary. Presentational values
 * only — the block formats nothing itself.
 */
export const toShiftSummary = (job: TOpenShiftJob): TShiftSummary => ({
  // The gate routes only `JobStatusEnum.Open` here, so the lifecycle state is
  // fixed rather than derived from a status table.
  status: 'open',
  // `local_date` already arrives in the shape the Figma draws ("Wed, Jul 9,
  // 2026"), so it passes through untouched.
  date: job.local_date,
  startTime: job.local_start_time,
  endTime: job.local_end_time,
  totalHours: computeHours(job.local_start_time, job.local_end_time) ?? '',
  position: resolveProfessionName(job.professionName, job.subProfession) ?? '',
  // The block appends its own `/hr` unit.
  rate: formatMoney(job.rate),
});

